import React from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import {
  Shield,
  Lock,
  Database,
  Users,
  Eye,
  FileText,
  Globe,
  Mail,
} from 'lucide-react';

const PrivacyPolicyPage: React.FC = () => {
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper elevation={2} sx={{ p: 4 }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h3" component="h1" gutterBottom>
            Privacy Policy
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Protecting Your Digital Identity and Data Sovereignty
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Last Updated: January 2025
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Shield size={28} />
            Our Commitment to Privacy
          </Typography>
          <Typography variant="body1" paragraph>
            DecentralizedKYC is built on the fundamental principle of user data sovereignty. We believe that you should have complete control over your identity data. Our decentralized architecture ensures that your personal information remains under your ownership while enabling secure, efficient identity verification across platforms.
          </Typography>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Database size={24} />
            Data Collection and Storage
          </Typography>
          <Typography variant="body1" paragraph>
            Unlike traditional centralized KYC systems, DecentralizedKYC employs a hybrid storage architecture:
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon><Lock size={20} /></ListItemIcon>
              <ListItemText 
                primary="Blockchain Storage" 
                secondary="Essential metadata and verification status are stored on the Ethereum blockchain, ensuring immutability and transparency."
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><Shield size={20} /></ListItemIcon>
              <ListItemText 
                primary="IPFS Encrypted Storage" 
                secondary="Sensitive identity documents are encrypted and stored on IPFS (InterPlanetary File System), providing decentralized, secure storage."
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><Users size={20} /></ListItemIcon>
              <ListItemText 
                primary="User-Controlled Access" 
                secondary="You maintain complete control over who can access your identity data through our smart contract permission system."
              />
            </ListItem>
          </List>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Eye size={24} />
            Data Access and Sharing
          </Typography>
          <Typography variant="body1" paragraph>
            Your identity NFT grants you unprecedented control over data sharing:
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="Granular Permissions" 
                secondary="You can grant specific organizations access to only the KYC data types they need (e.g., basic identity, financial verification, professional credentials)."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Revocable Access" 
                secondary="You can revoke access permissions at any time through your wallet interface."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Audit Trail" 
                secondary="All data access requests and approvals are recorded on the blockchain for complete transparency."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="No Data Sale" 
                secondary="We never sell, rent, or monetize your personal data. Your information is used solely for identity verification purposes."
              />
            </ListItem>
          </List>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FileText size={24} />
            Data Processing and Verification
          </Typography>
          <Typography variant="body1" paragraph>
            Our KYC verification process is designed with privacy-by-design principles:
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="Minimal Data Collection" 
                secondary="We collect only the minimum data necessary for each specific KYC template and verification level."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Authorized Reviewers Only" 
                secondary="Only whitelisted addresses with appropriate permissions can review KYC submissions."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Secure Processing" 
                secondary="All data processing occurs in secure environments with end-to-end encryption."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Data Retention" 
                secondary="Verified data is retained only as long as necessary for compliance and service provision. You can request data deletion subject to regulatory requirements."
              />
            </ListItem>
          </List>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Globe size={24} />
            Third-Party Integrations
          </Typography>
          <Typography variant="body1" paragraph>
            When you use your DecentralizedKYC identity with third-party platforms:
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="Explicit Consent" 
                secondary="Each data sharing request requires your explicit consent through wallet signature."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Limited Scope" 
                secondary="Third parties receive only the specific KYC data types they are authorized to access."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="No Direct Access" 
                secondary="Third parties cannot directly access your raw identity documents; they receive only verification status and approved data points."
              />
            </ListItem>
          </List>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            Your Rights and Controls
          </Typography>
          <Typography variant="body1" paragraph>
            As the owner of your identity NFT, you have the following rights:
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="Right to Access" 
                secondary="View all your stored data and access history at any time."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Right to Rectification" 
                secondary="Update or correct your identity information through re-verification."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Right to Portability" 
                secondary="Your identity NFT is fully portable across compatible platforms and services."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Right to Erasure" 
                secondary="Request deletion of your data, subject to legal and regulatory requirements."
              />
            </ListItem>
          </List>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            Security Measures
          </Typography>
          <Typography variant="body1" paragraph>
            We implement industry-leading security measures to protect your data:
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="Smart Contract Security" 
                secondary="Our contracts are audited by leading security firms and implement OpenZeppelin security standards."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Encryption" 
                secondary="All sensitive data is encrypted both in transit and at rest using AES-256 encryption."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Access Controls" 
                secondary="Multi-layered access controls ensure only authorized parties can access specific data types."
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Regular Audits" 
                secondary="We conduct regular security audits and vulnerability assessments."
              />
            </ListItem>
          </List>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            Compliance and Regulations
          </Typography>
          <Typography variant="body1" paragraph>
            DecentralizedKYC is designed to comply with major privacy regulations including GDPR, CCPA, and other applicable data protection laws. We work closely with legal experts to ensure our decentralized approach meets regulatory requirements while maximizing user privacy and control.
          </Typography>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            Updates to This Policy
          </Typography>
          <Typography variant="body1" paragraph>
            We may update this Privacy Policy to reflect changes in our practices or applicable laws. Significant changes will be communicated through our official channels and updated on our platform. Continued use of DecentralizedKYC after such changes constitutes acceptance of the updated policy.
          </Typography>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <Mail size={24} />
            Contact Us
          </Typography>
          <Typography variant="body1" paragraph>
            If you have any questions about this Privacy Policy or our data practices, please contact us:
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Email: privacy@dekyc.org<br />
            Website: https://dekyc.org<br />
            Community: https://discord.gg/dekyc
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default PrivacyPolicyPage;